import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteIcon from '@mui/icons-material/Delete';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import { ContentPageLayout } from '../components/Shared';
import {
  ProgressionLocaleFields,
  emptyProgressionLocales,
  progressionLocalesFromEntity,
  progressionPayloadFromLocales,
} from '../components/ProgressionLocaleFields';
import { listDocuments, type DocumentRow } from '../services/documentApi';
import {
  appendLessonDocuments,
  getLesson,
  removeLessonDocument,
  reorderLessonDocuments,
  updateLesson,
  type TrainingLesson,
  type ValidationStep,
} from '../services/formationsAdminApi';

const LessonEditorPage: React.FC = () => {
  const { formationId, levelId, lessonId } = useParams<{
    formationId: string;
    levelId: string;
    lessonId: string;
  }>();
  const { t } = useTranslation();
  const { navigateWithLang } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [lesson, setLesson] = useState<TrainingLesson | null>(null);
  const [locales, setLocales] = useState(emptyProgressionLocales());
  const [code, setCode] = useState('');
  const [docOptions, setDocOptions] = useState<DocumentRow[]>([]);
  const [pickedDoc, setPickedDoc] = useState<DocumentRow | null>(null);
  const [newStepType, setNewStepType] = useState('content_ack');
  const [newStepTitle, setNewStepTitle] = useState('');

  const load = useCallback(async () => {
    if (!lessonId) return;
    setLoading(true);
    setError(null);
    try {
      const [lessonRes, docsRes] = await Promise.all([
        getLesson(lessonId),
        listDocuments({ limit: 200 }),
      ]);
      const l = lessonRes.data?.lesson;
      if (!l) throw new Error('Lesson not found');
      setLesson(l);
      setCode(l.code);
      setLocales(
        progressionLocalesFromEntity({
          name: l.locales?.en?.title,
          description: l.locales?.en?.summary,
          locales: {
            en: { name: l.locales?.en?.title || '', description: l.locales?.en?.summary || '' },
            fr: { name: l.locales?.fr?.title || '', description: l.locales?.fr?.summary || '' },
          },
        }),
      );
      const docs = docsRes.data?.documents || [];
      setDocOptions(
        docs.filter(
          (d) =>
            d.docDomain === 'TCH-TRAIN' ||
            (d.code && d.code.startsWith('TCH-')),
        ),
      );
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load lesson');
    } finally {
      setLoading(false);
    }
  }, [lessonId]);

  useEffect(() => {
    load();
  }, [load]);

  const saveMetadata = async () => {
    if (!lessonId) return;
    setSaving(true);
    setMsg(null);
    try {
      const payload = progressionPayloadFromLocales(locales);
      const res = await updateLesson(lessonId, {
        code,
        locales: {
          en: { title: payload.locales.en.name, summary: payload.locales.en.description, objectives: [] },
          fr: { title: payload.locales.fr.name, summary: payload.locales.fr.description, objectives: [] },
        },
      });
      if (res.data?.lesson) setLesson(res.data.lesson);
      setMsg(t('formations.saved', { defaultValue: 'Saved' }));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const addDocument = async () => {
    if (!lessonId || !pickedDoc?._id) return;
    setSaving(true);
    try {
      const res = await appendLessonDocuments(lessonId, [
        { documentId: pickedDoc._id, documentCode: pickedDoc.code },
      ]);
      if (res.data?.lesson) setLesson(res.data.lesson);
      setPickedDoc(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to add document');
    } finally {
      setSaving(false);
    }
  };

  const moveDocument = async (index: number, direction: -1 | 1) => {
    if (!lesson || !lessonId) return;
    const docs = [...lesson.documents].sort((a, b) => a.order - b.order);
    const target = index + direction;
    if (target < 0 || target >= docs.length) return;
    [docs[index], docs[target]] = [docs[target], docs[index]];
    const orderedIds = docs.map((d) => d._id);
    setSaving(true);
    try {
      const res = await reorderLessonDocuments(lessonId, orderedIds);
      if (res.data?.lesson) setLesson(res.data.lesson);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Reorder failed');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveDocument = async (subId: string) => {
    if (!lessonId || !lesson) return;
    setSaving(true);
    try {
      const res = await removeLessonDocument(lessonId, subId, lesson);
      if (res.data?.lesson) setLesson(res.data.lesson);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Remove failed');
    } finally {
      setSaving(false);
    }
  };

  const addValidationStep = async () => {
    if (!lessonId || !lesson) return;
    const steps = [...(lesson.validationSteps || [])];
    steps.push({
      order: steps.length + 1,
      type: newStepType,
      locales: {
        en: { title: newStepTitle.trim() || newStepType, instructions: '' },
        fr: { title: newStepTitle.trim() || newStepType, instructions: '' },
      },
    } as ValidationStep);
    setSaving(true);
    try {
      const res = await updateLesson(lessonId, { validationSteps: steps });
      if (res.data?.lesson) setLesson(res.data.lesson);
      setNewStepTitle('');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to add step');
    } finally {
      setSaving(false);
    }
  };

  const removeValidationStep = async (stepId: string) => {
    if (!lessonId || !lesson) return;
    const steps = (lesson.validationSteps || []).filter((s) => s._id !== stepId);
    setSaving(true);
    try {
      const res = await updateLesson(lessonId, { validationSteps: steps });
      if (res.data?.lesson) setLesson(res.data.lesson);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to remove step');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <ContentPageLayout>
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      </ContentPageLayout>
    );
  }

  if (!lesson) {
    return (
      <ContentPageLayout>
        <Alert severity="error">{error || 'Lesson not found'}</Alert>
      </ContentPageLayout>
    );
  }

  const sortedDocs = [...lesson.documents].sort((a, b) => a.order - b.order);
  const sortedSteps = [...(lesson.validationSteps || [])].sort((a, b) => a.order - b.order);

  return (
    <ContentPageLayout>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigateWithLang(`/formations/${formationId}/levels/${levelId}`)}
        sx={{ mb: 2 }}
      >
        {t('formations.backLevel', { defaultValue: 'Back to level' })}
      </Button>

      <Typography variant="h5" gutterBottom sx={{'color': 'primary.main', 'fontWeight': 'bold'}}>
        {lesson.code}
      </Typography>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {msg ? <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg}</Alert> : null}

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('formations.lesson.metadata', { defaultValue: 'Metadata' })}
            </Typography>
            <TextField
              fullWidth
              size="small"
              label={t('formations.fields.code', { defaultValue: 'Code' })}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              sx={{ mb: 2 }}
            />
            <ProgressionLocaleFields locales={locales} onChange={setLocales} />
            <Button variant="contained" sx={{ mt: 2 }} onClick={saveMetadata} disabled={saving}>
              {t('formations.save', { defaultValue: 'Save' })}
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('formations.lesson.documents', { defaultValue: 'Documents (reading order)' })}
            </Typography>

            <Box display="flex" gap={1} mb={2} flexWrap="wrap" alignItems="center">
              <Autocomplete
                sx={{ minWidth: 280, flex: 1 }}
                options={docOptions}
                getOptionLabel={(o) => `${o.code || o._id} — ${o.title || ''}`}
                value={pickedDoc}
                onChange={(_, v) => setPickedDoc(v)}
                renderInput={(params) => (
                  <TextField {...params} size="small" label={t('formations.lesson.pickDocument', { defaultValue: 'Add document' })} />
                )}
              />
              <Button variant="outlined" disabled={saving || !pickedDoc} onClick={addDocument}>
                {t('formations.lesson.addDocument', { defaultValue: 'Add' })}
              </Button>
            </Box>

            {sortedDocs.length === 0 ? (
              <Typography color="text.secondary">
                {t('formations.lesson.noDocuments', { defaultValue: 'No documents linked yet.' })}
              </Typography>
            ) : (
              sortedDocs.map((doc, index) => (
                <Box
                  key={doc._id}
                  display="flex"
                  alignItems="center"
                  gap={1}
                  py={1}
                  borderBottom="1px solid"
                  borderColor="divider"
                >
                  <Typography variant="body2" sx={{ minWidth: 24 }}>
                    {index + 1}.
                  </Typography>
                  <Box flex={1}>
                    <Typography variant="body2" fontWeight={600}>
                      {doc.documentCode || doc.documentId}
                    </Typography>
                  </Box>
                  <IconButton size="small" disabled={index === 0 || saving} onClick={() => moveDocument(index, -1)}>
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    disabled={index === sortedDocs.length - 1 || saving}
                    onClick={() => moveDocument(index, 1)}
                  >
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" disabled={saving} onClick={() => handleRemoveDocument(doc._id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('formations.lesson.validationSteps', { defaultValue: 'Validation steps (end of lesson)' })}
            </Typography>
            <Box display="flex" gap={1} mb={2} flexWrap="wrap" alignItems="center">
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Type</InputLabel>
                <Select value={newStepType} label="Type" onChange={(e) => setNewStepType(e.target.value)}>
                  <MenuItem value="content_ack">content_ack</MenuItem>
                  <MenuItem value="quiz">quiz</MenuItem>
                  <MenuItem value="exercise">exercise</MenuItem>
                  <MenuItem value="teacher_action">teacher_action</MenuItem>
                  <MenuItem value="charter">charter</MenuItem>
                  <MenuItem value="admin_verification">admin_verification</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                label={t('formations.lesson.stepTitle', { defaultValue: 'Title' })}
                value={newStepTitle}
                onChange={(e) => setNewStepTitle(e.target.value)}
              />
              <Button variant="outlined" disabled={saving} onClick={addValidationStep}>
                {t('formations.lesson.addStep', { defaultValue: 'Add step' })}
              </Button>
            </Box>
            {sortedSteps.length === 0 ? (
              <Typography color="text.secondary">
                {t('formations.lesson.noSteps', { defaultValue: 'No validation steps yet.' })}
              </Typography>
            ) : (
              sortedSteps.map((step, index) => (
                <Box
                  key={step._id}
                  display="flex"
                  alignItems="center"
                  gap={1}
                  py={1}
                  borderBottom="1px solid"
                  borderColor="divider"
                >
                  <Typography variant="body2" sx={{ minWidth: 24 }}>{index + 1}.</Typography>
                  <Box flex={1}>
                    <Typography variant="body2" fontWeight={600}>
                      {step.locales?.fr?.title || step.locales?.en?.title || step.type}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{step.type}</Typography>
                  </Box>
                  <IconButton size="small" color="error" disabled={saving} onClick={() => removeValidationStep(step._id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))
            )}
          </Paper>
        </Grid>
      </Grid>
    </ContentPageLayout>
  );
};

export default LessonEditorPage;
